import React from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useJourney } from "@/context/JourneyContext";
import { StageFrame, Eyebrow, EASE } from "@/components/StageFrame";
import { ConsultantNav } from "@/components/Navigation";
import { money } from "@/lib/api";
import { Target, Building2, Wallet, Gauge, MapPin, Clock, TrendingUp, Coins, LineChart, CheckCircle2 } from "lucide-react";

const OBJECTIVE_LABEL = {
  "Capital growth": "Capital growth",
  "Strong rental income": "Rental income",
  "Balance of both": "Growth + income",
  "First step into investing": "First step / balanced",
};

const Row = ({ icon: Icon, label, value, delay }) => (
  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE, delay }}
    className="flex items-start gap-3 rounded-xl border border-line bg-white/[0.03] p-4">
    <div className="grid place-items-center w-9 h-9 rounded-lg bg-brand/15 text-brand shrink-0"><Icon size={16} /></div>
    <div>
      <div className="label-eyebrow text-mutedText">{label}</div>
      <div className="text-forest font-medium mt-0.5">{value}</div>
    </div>
  </motion.div>
);

const RecCard = ({ icon: Icon, tag, title, line, delay }) => (
  <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE, delay }}
    className="rounded-2xl border border-line bg-white/[0.03] p-5">
    <div className="flex items-center justify-center w-11 h-11 rounded-2xl bg-brand/15 text-brand"><Icon size={20} /></div>
    <span className="label-eyebrow text-brand mt-4 block">{tag}</span>
    <h3 className="font-serif text-xl text-forest mt-1">{title}</h3>
    <p className="text-sm text-mutedText mt-2 leading-relaxed">{line}</p>
  </motion.div>
);

export default function InvestorProfile() {
  const { data, next, back } = useJourney();
  const a = data.investor_discovery || {};

  const objective = OBJECTIVE_LABEL[a.objective] || "Balanced";
  const experience = a.experience === "I already own property"
    ? `Experienced${a.owned_count ? ` (${a.owned_count} owned)` : ""}`
    : "First investor";
  const funds = money(a.funds) + (a.equity_available ? " + equity" : "");
  const location = a.location_stance === "I have a preferred location"
    ? (a.preferred_location || "Preferred location")
    : "Open to recommendation";
  const future = a.future_strategy ? "Yes — grow the portfolio" : "Not right now";

  const statement = `A ${experience.toLowerCase()} focused on ${objective.toLowerCase()}, comfortable to about ${a.contribution || "$0–$50"}/week, holding ${a.horizon || "long term"}.`;

  const goNext = () => next();

  return (
    <StageFrame testId="stage-investor-profile" className="px-6 md:px-14 pt-16 pb-28">
      <div className="max-w-5xl mx-auto">
        <Eyebrow>Your Investor Profile</Eyebrow>
        <h1 className="font-serif text-3xl md:text-5xl font-light tracking-tight text-forest mt-3 max-w-3xl">
          {statement}
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-8" data-testid="investor-profile-grid">
          <Row icon={Target} label="Objective" value={objective} delay={0.04} />
          <Row icon={Building2} label="Experience" value={experience} delay={0.08} />
          <Row icon={Wallet} label="Funds available" value={funds} delay={0.12} />
          <Row icon={Gauge} label="Comfortable contribution" value={`Up to ${a.contribution || "$0–$50"}/week`} delay={0.16} />
          <Row icon={MapPin} label="Location" value={location} delay={0.2} />
          <Row icon={Clock} label="Time horizon" value={a.horizon || "Long term"} delay={0.24} />
          <Row icon={TrendingUp} label="Future property strategy" value={future} delay={0.28} />
        </div>

        <div className="mt-12">
          <div className="flex items-center gap-2 text-forest/80">
            <CheckCircle2 size={18} className="text-brand" />
            <span className="text-sm">From here, Propintellix finds the three properties that best fit this objective.</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
            <RecCard icon={TrendingUp} tag="Best overall" title="The balanced pick"
              line="Best combination of growth, rent and investor affordability for your position." delay={0.05} />
            <RecCard icon={Coins} tag="Strongest cash flow" title="Easiest to hold"
              line="Lowest estimated weekly contribution and the strongest rental yield." delay={0.12} />
            <RecCard icon={LineChart} tag="Strongest growth" title="Best long-term position"
              line="The location and fundamentals with the strongest long-term growth outlook." delay={0.19} />
          </div>
          <p className="text-xs text-mutedText/70 mt-4">Your three matched properties appear once you've chosen a location and floor plan.</p>
        </div>
      </div>

      <ConsultantNav testId="stage-investor-profile" onBack={back} onNext={goNext} nextLabel="Show me the locations" />
    </StageFrame>
  );
}
